import type { Concept2WorkoutSegment, Concept2WorkoutStroke } from '../types/concept2';
import { paceDistanceForMachine, parseConcept2HeartRate } from './concept2Metrics';

export interface Concept2WorkoutSeriesPoint {
  timeSeconds: number;
  paceSeconds: number | null;
  strokeRate: number | null;
  heartRate: number | null;
}

export interface Concept2WorkoutSeries {
  points: Concept2WorkoutSeriesPoint[];
  totalSeconds: number;
  paceUnitLabel: string;
  source: 'strokes' | 'splits';
}

const CHART_POINT_LIMIT = 1_500;

function paceSecondsPerUnit(
  timeTenths: number | null | undefined,
  distanceMeters: number | null | undefined,
  machineType: string | null | undefined
): number | null {
  const paceDistance = paceDistanceForMachine(machineType);
  if (
    paceDistance === null ||
    timeTenths == null ||
    timeTenths <= 0 ||
    distanceMeters == null ||
    distanceMeters <= 0
  ) {
    return null;
  }

  return (timeTenths / 10) * (paceDistance / distanceMeters);
}

export function formatConcept2PaceAxisLabel(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

function downsampleSeriesPoints(
  points: Concept2WorkoutSeriesPoint[]
): Concept2WorkoutSeriesPoint[] {
  if (points.length <= CHART_POINT_LIMIT) {
    return points;
  }

  const stride = Math.ceil(points.length / CHART_POINT_LIMIT);
  const sampled = points.filter((_, index) => index % stride === 0);
  const lastPoint = points[points.length - 1];
  if (sampled[sampled.length - 1] !== lastPoint) {
    sampled.push(lastPoint);
  }
  return sampled;
}

function buildStrokeTimeSeconds(
  strokes: Concept2WorkoutStroke[],
  fallbackTimeTenths?: number | null
): number[] {
  let cumulativeFromIncremental = 0;
  const fromIncremental = strokes.map((stroke) => {
    if (stroke.incremental_time_tenths != null) {
      cumulativeFromIncremental += stroke.incremental_time_tenths;
    }
    return cumulativeFromIncremental / 10;
  });

  const fromStoredWorkoutTime = strokes.map((stroke) => stroke.workout_time_tenths / 10);
  const workoutCapSeconds =
    fallbackTimeTenths != null && fallbackTimeTenths > 0 ? fallbackTimeTenths / 10 : null;

  const incrementalTotal = fromIncremental[fromIncremental.length - 1] ?? 0;
  const storedTotal = fromStoredWorkoutTime[fromStoredWorkoutTime.length - 1] ?? 0;

  if (workoutCapSeconds != null) {
    const incrementalWithinCap = incrementalTotal <= workoutCapSeconds * 1.1;
    const storedWithinCap = storedTotal <= workoutCapSeconds * 1.1;

    if (incrementalWithinCap && !storedWithinCap) {
      return fromIncremental;
    }

    if (storedWithinCap && !incrementalWithinCap) {
      return fromStoredWorkoutTime;
    }

    if (!incrementalWithinCap && !storedWithinCap && incrementalTotal > 0) {
      const scale = workoutCapSeconds / incrementalTotal;
      return fromIncremental.map((timeSeconds) => timeSeconds * scale);
    }
  }

  if (storedTotal > 0 && incrementalTotal > storedTotal * 1.5) {
    return fromStoredWorkoutTime;
  }

  return fromIncremental;
}

function buildConcept2StrokeSeries(
  strokes: Concept2WorkoutStroke[],
  machineType: string,
  fallbackTimeTenths?: number | null
): Concept2WorkoutSeries | null {
  if (strokes.length < 2) {
    return null;
  }

  const paceUnitLabel = paceDistanceForMachine(machineType) === 1000 ? '/1000 m' : '/500 m';
  const timeSecondsSeries = buildStrokeTimeSeconds(strokes, fallbackTimeTenths);
  const points = downsampleSeriesPoints(
    strokes.map((stroke, index) => ({
      timeSeconds: timeSecondsSeries[index] ?? 0,
      paceSeconds: stroke.pace_tenths != null ? stroke.pace_tenths / 10 : null,
      strokeRate: stroke.stroke_rate,
      heartRate: stroke.heart_rate,
    }))
  );

  const workoutCapSeconds =
    fallbackTimeTenths != null && fallbackTimeTenths > 0 ? fallbackTimeTenths / 10 : null;
  const lastPointSeconds = points[points.length - 1]?.timeSeconds ?? 0;
  const totalSeconds = workoutCapSeconds ?? lastPointSeconds;

  return {
    points,
    totalSeconds,
    paceUnitLabel,
    source: 'strokes',
  };
}

function buildConcept2SplitSeries(
  segments: Concept2WorkoutSegment[],
  machineType: string,
  fallbackTimeTenths?: number | null
): Concept2WorkoutSeries | null {
  const paceUnitLabel = paceDistanceForMachine(machineType) === 1000 ? '/1000 m' : '/500 m';
  const splits = segments
    .filter((segment) => segment.segment_type === 'split')
    .sort((left, right) => left.sequence - right.sequence);

  if (splits.length === 0) {
    return null;
  }

  let cumulativeSeconds = 0;
  const boundaryPoints: Concept2WorkoutSeriesPoint[] = [];

  for (const split of splits) {
    const splitSeconds = (split.time_tenths ?? 0) / 10;
    cumulativeSeconds += splitSeconds;
    const heartRate = parseConcept2HeartRate(split.heart_rate);

    boundaryPoints.push({
      timeSeconds: cumulativeSeconds,
      paceSeconds: paceSecondsPerUnit(
        split.time_tenths,
        split.distance_meters,
        split.machine_type || machineType
      ),
      strokeRate: split.stroke_rate,
      heartRate: heartRate.average,
    });
  }

  const firstSplit = splits[0];
  const firstHeartRate = parseConcept2HeartRate(firstSplit.heart_rate);
  const points: Concept2WorkoutSeriesPoint[] = [
    {
      timeSeconds: 0,
      paceSeconds: paceSecondsPerUnit(
        firstSplit.time_tenths,
        firstSplit.distance_meters,
        firstSplit.machine_type || machineType
      ),
      strokeRate: firstSplit.stroke_rate,
      heartRate: firstHeartRate.average,
    },
    ...boundaryPoints,
  ];

  const totalSeconds =
    cumulativeSeconds > 0
      ? cumulativeSeconds
      : fallbackTimeTenths != null
        ? fallbackTimeTenths / 10
        : (points[points.length - 1]?.timeSeconds ?? 0);

  return {
    points,
    totalSeconds,
    paceUnitLabel,
    source: 'splits',
  };
}

export function buildConcept2WorkoutSeries(
  segments: Concept2WorkoutSegment[],
  machineType: string,
  fallbackTimeTenths?: number | null,
  strokes: Concept2WorkoutStroke[] = []
): Concept2WorkoutSeries | null {
  const strokeSeries = buildConcept2StrokeSeries(strokes, machineType, fallbackTimeTenths);
  if (strokeSeries) {
    return strokeSeries;
  }

  return buildConcept2SplitSeries(segments, machineType, fallbackTimeTenths);
}

export function concept2SeriesHasMetric(
  points: Concept2WorkoutSeriesPoint[],
  key: keyof Pick<Concept2WorkoutSeriesPoint, 'paceSeconds' | 'strokeRate' | 'heartRate'>
): boolean {
  return points.some((point) => point[key] != null && Number.isFinite(point[key] as number));
}
